import React, { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ExclamationCircleIcon, PlusCircleIcon, ServerIcon, SyncAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaDeviceCores } from '../../../hooks/useAdminSplaDeviceCores';
import { formatWallClockDateTime } from '../../../utils/dateFormat';
import type {
  SplaDeviceCores,
  SplaDeviceCoresCreateRequest,
  SplaDeviceCoresUpdateRequest,
} from '../../../types/models/Spla';
import type {
  ApiResponseSplaDeviceCoresDeleted,
  ApiResponseSplaDeviceCoresSingle,
} from '../../../types/api/ApiResponseSpla';

const NOTE_MAX = 255;

// Backend field-error keys rendered inline on the matching input; anything else (body, id, …)
// lands in the form-level alert.
const FIELD_ERROR_KEYS = new Set(['netbox_device_id', 'device_name', 'cpu_cores', 'note']);

interface DeviceModalProps {
  /** `null` = create; a row = edit (parent keys the mount so state is always fresh). */
  device: SplaDeviceCores | null;
  onClose: () => void;
  onSaved: (device: SplaDeviceCores, isCreate: boolean) => void;
}

// Create/edit one registry row. The NetBox device id is the identity — immutable after create
// (disabled on edit); a duplicate id gets the backend's field error pointing at PATCH.
function SplaDeviceCoresModal({ device, onClose, onSaved }: DeviceModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const isEdit = device !== null;

  const [netboxId, setNetboxId] = useState(device !== null ? String(device.netbox_device_id) : '');
  const [deviceName, setDeviceName] = useState(device?.device_name ?? '');
  const [cpuCores, setCpuCores] = useState(device !== null ? String(device.cpu_cores) : '');
  const [note, setNote] = useState(device?.note ?? '');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const trimmedId = netboxId.trim();
  const trimmedName = deviceName.trim();
  const trimmedCores = cpuCores.trim();
  const trimmedNote = note.trim();

  const isIdValid = /^[1-9]\d*$/.test(trimmedId);
  const isNameValid = trimmedName.length > 0;
  const isCoresValid = /^[1-9]\d*$/.test(trimmedCores);
  const isNoteValid = trimmedNote.length <= NOTE_MAX;

  const buildUpdateBody = (original: SplaDeviceCores): SplaDeviceCoresUpdateRequest => {
    const body: SplaDeviceCoresUpdateRequest = {};
    if (trimmedName !== original.device_name) {
      body.device_name = trimmedName;
    }
    if (Number(trimmedCores) !== original.cpu_cores) {
      body.cpu_cores = Number(trimmedCores);
    }
    if (trimmedNote !== (original.note ?? '')) {
      body.note = trimmedNote === '' ? null : trimmedNote;
    }
    return body;
  };

  const hasChanges = device === null || Object.keys(buildUpdateBody(device)).length > 0;
  const isFormValid = (isEdit || isIdValid) && isNameValid && isCoresValid && isNoteValid;
  const canSubmit = !isSaving && isFormValid && hasChanges;

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const createBody: SplaDeviceCoresCreateRequest = {
        netbox_device_id: Number(trimmedId),
        device_name: trimmedName,
        cpu_cores: Number(trimmedCores),
        ...(trimmedNote !== '' ? { note: trimmedNote } : {}),
      };
      const response = await apiFetch(
        device !== null ? `/admin/spla/device-cores/${device.id}` : '/admin/spla/device-cores',
        {
          method: device !== null ? 'PATCH' : 'POST',
          body: JSON.stringify(device !== null ? buildUpdateBody(device) : createBody),
        },
      );
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaDeviceCoresSingle | null;
      if (body?.status === 'success' && body.data?.device) {
        const saved = body.data.device;
        onSaved(saved, !isEdit);
        addNotification({
          title: isEdit ? 'Device updated' : 'Device registered',
          srTitle: 'Success alert',
          description: `${saved.device_name} — ${saved.cpu_cores} physical cores. Dependent bindings pick this up on the next recompute.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onClose();
        return;
      }

      const errors: Record<string, string> = {};
      const generalParts: string[] = [];
      if (body?.data) {
        for (const [key, value] of Object.entries(body.data)) {
          if (typeof value !== 'string') {
            continue;
          }
          if (FIELD_ERROR_KEYS.has(key)) {
            errors[key] = value;
          } else {
            generalParts.push(value);
          }
        }
      }
      const general =
        generalParts.length > 0
          ? generalParts.join(' ')
          : Object.keys(errors).length === 0
            ? (body?.message ?? 'Could not save the device. Please try again.')
            : null;
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update the device' : 'Could not register the device',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update the device' : 'Could not register the device',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const fieldHelper = (key: string, fallback?: string) =>
    (fieldErrors[key] !== undefined || fallback !== undefined) && (
      <FormHelperText>
        <HelperText>
          <HelperTextItem variant={fieldErrors[key] !== undefined ? 'error' : 'default'}>
            {fieldErrors[key] ?? fallback}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    );

  return (
    <Modal
      variant="small"
      isOpen
      onClose={onClose}
      aria-label={isEdit ? `Edit device ${device.device_name}` : 'Register a device'}
    >
      <ModalHeader
        title={isEdit ? `Edit device — ${device.device_name}` : 'Register a device'}
        description="Physical cores, never vCPUs — the SPLA licensing basis."
      />
      <ModalBody>
        <Form
          id="spla-device-cores-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          {formError !== null && (
            <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Registration failed'}>
              {formError}
            </Alert>
          )}

          <FormGroup label="NetBox device id" isRequired fieldId="spla-device-netbox-id">
            <TextInput
              id="spla-device-netbox-id"
              type="text"
              inputMode="numeric"
              value={netboxId}
              placeholder="22"
              onChange={(_event, value) => {
                setNetboxId(value);
                clearFieldError('netbox_device_id');
              }}
              validated={
                fieldErrors['netbox_device_id'] !== undefined || (trimmedId !== '' && !isIdValid)
                  ? 'error'
                  : 'default'
              }
              isDisabled={isSaving || isEdit}
            />
            {fieldHelper(
              'netbox_device_id',
              isEdit
                ? 'Immutable — one registry row per NetBox device.'
                : trimmedId !== '' && !isIdValid
                  ? 'Enter the positive numeric NetBox device id.'
                  : 'One registry row per NetBox device.',
            )}
          </FormGroup>

          <FormGroup label="Device name" isRequired fieldId="spla-device-name">
            <TextInput
              id="spla-device-name"
              type="text"
              value={deviceName}
              placeholder="palpatine.ancalagon.be"
              onChange={(_event, value) => {
                setDeviceName(value);
                clearFieldError('device_name');
              }}
              validated={fieldErrors['device_name'] !== undefined ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {fieldHelper('device_name')}
          </FormGroup>

          <FormGroup label="Physical CPU cores" isRequired fieldId="spla-device-cores">
            <TextInput
              id="spla-device-cores"
              type="text"
              inputMode="numeric"
              value={cpuCores}
              placeholder="16"
              onChange={(_event, value) => {
                setCpuCores(value);
                clearFieldError('cpu_cores');
              }}
              validated={
                fieldErrors['cpu_cores'] !== undefined || (trimmedCores !== '' && !isCoresValid)
                  ? 'error'
                  : 'default'
              }
              isDisabled={isSaving}
            />
            {fieldHelper(
              'cpu_cores',
              trimmedCores !== '' && !isCoresValid
                ? 'Enter a positive whole number.'
                : 'All sockets combined — never vCPUs.',
            )}
          </FormGroup>

          <FormGroup label="Note" fieldId="spla-device-note">
            <TextInput
              id="spla-device-note"
              type="text"
              value={note}
              placeholder="AMD EPYC 16c"
              onChange={(_event, value) => {
                setNote(value);
                clearFieldError('note');
              }}
              validated={fieldErrors['note'] !== undefined || !isNoteValid ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {fieldHelper(
              'note',
              !isNoteValid
                ? `At most ${NOTE_MAX} characters.`
                : `Optional.${isEdit ? ' Clearing it removes the note.' : ''}`,
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="spla-device-cores-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Register'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface DeleteModalProps {
  device: SplaDeviceCores;
  onClose: () => void;
  /** Performs the DELETE; resolves `true` on success (the parent owns the toasts). */
  onDelete: (device: SplaDeviceCores) => Promise<boolean>;
}

// The backend blocks deletion while an `own_cores_half` binding targets the device — that refusal
// is surfaced verbatim by the parent (deliberately not pre-computed client-side).
function SplaDeviceCoresDeleteModal({ device, onClose, onDelete }: DeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    setIsDeleting(true);
    const ok = await onDelete(device);
    setIsDeleting(false);
    if (ok) {
      onClose();
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose} aria-label={`Delete device ${device.device_name}`}>
      <ModalHeader title="Delete device" titleIconVariant="warning" />
      <ModalBody>
        <Content component="p">
          Remove <strong>{device.device_name}</strong> (NetBox #{device.netbox_device_id},{' '}
          {device.cpu_cores} cores) from the registry?
        </Content>
        <Content component="p">
          Bindings computing from this host&apos;s cores fail loud on their next pass instead of silently
          dropping a license off the report.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleConfirm()} isLoading={isDeleting} isDisabled={isDeleting}>
          Delete
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDeleting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface SplaDeviceCoresTabProps {
  /** Defers every admin fetch until the page's staff check has passed. */
  isStaff: boolean;
}

/**
 * The device-core registry — the physical CPU-core counts the auto-computation licenses against.
 * NetBox's agent-maintained `core_count` custom field is the fallback; this registry OVERRIDES it
 * and covers agent-less hosts.
 */
function SplaDeviceCoresTab({ isStaff }: SplaDeviceCoresTabProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { devices, loading, error, refetch, applyDevice, removeDevice } = useAdminSplaDeviceCores(isStaff);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editDevice, setEditDevice] = useState<SplaDeviceCores | null>(null);
  const [deleteDevice, setDeleteDevice] = useState<SplaDeviceCores | null>(null);

  const handleSaved = useCallback(
    (device: SplaDeviceCores, isCreate: boolean) => {
      if (isCreate) {
        void refetch();
      } else {
        applyDevice(device);
      }
    },
    [refetch, applyDevice],
  );

  const handleDelete = useCallback(
    async (device: SplaDeviceCores): Promise<boolean> => {
      try {
        const response = await apiFetch(`/admin/spla/device-cores/${device.id}`, { method: 'DELETE' });
        // Branch on the JSend body.status, NEVER response.ok.
        const body = (await response.json().catch(() => null)) as ApiResponseSplaDeviceCoresDeleted | null;
        if (body?.status === 'success' && body.data?.deleted) {
          removeDevice(device.id);
          addNotification({
            title: 'Device removed',
            srTitle: 'Success alert',
            description: `${device.device_name} left the core registry.`,
            variant: 'success',
          });
          setDrawerOpen(true);
          return true;
        }
        // The own_cores_half dependency block arrives under data.device — surfaced verbatim.
        addNotification({
          title: 'Could not remove the device',
          srTitle: 'Error alert',
          description: body?.data?.device ?? body?.message ?? 'Could not remove the device. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      } catch (err) {
        addNotification({
          title: 'Could not remove the device',
          srTitle: 'Error alert',
          description: err instanceof Error ? err.message : 'An unexpected error occurred.',
          variant: 'danger',
        });
        setDrawerOpen(true);
        return false;
      }
    },
    [apiFetch, removeDevice, addNotification, setDrawerOpen],
  );

  const rowActions = (device: SplaDeviceCores): IAction[] => [
    {
      title: 'Edit',
      onClick: () => setEditDevice(device),
    },
    {
      title: 'Delete',
      isDanger: true,
      onClick: () => setDeleteDevice(device),
    },
  ];

  const renderContent = () => {
    if (loading && devices.length === 0) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the registry"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (devices.length === 0) {
      return (
        <EmptyState titleText="No devices registered" icon={ServerIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Register each SPLA-licensed host&apos;s physical core count so bindings can compute quantities.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Register device
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <Table aria-label="Device-core registry" variant="compact">
        <Thead>
          <Tr>
            <Th>Device</Th>
            <Th>NetBox id</Th>
            <Th>Physical cores</Th>
            <Th>Note</Th>
            <Th>Updated</Th>
            <Th screenReaderText="Row actions" />
          </Tr>
        </Thead>
        <Tbody>
          {devices.map((device) => (
            <Tr key={device.id}>
              <Td dataLabel="Device">{device.device_name}</Td>
              <Td dataLabel="NetBox id">{device.netbox_device_id}</Td>
              <Td dataLabel="Physical cores">{device.cpu_cores}</Td>
              <Td dataLabel="Note">
                {device.note !== null ? <Content component="small">{device.note}</Content> : ''}
              </Td>
              <Td dataLabel="Updated">{formatWallClockDateTime(device.updated_at)}</Td>
              <Td isActionCell>
                <ActionsColumn items={rowActions(device)} />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Alert
        variant="info"
        isInline
        isPlain
        title="Physical cores (never vCPUs). This registry overrides the agent-reported NetBox core_count custom field — and covers agent-less hosts. A core edit propagates on the next bindings recompute."
        className="pf-v6-u-mb-md"
      />
      <Toolbar>
        <ToolbarContent>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                onClick={() => setIsCreateOpen(true)}
                isDisabled={loading && devices.length === 0}
              >
                Register device
              </Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button
                variant="secondary"
                icon={<SyncAltIcon />}
                onClick={() => void refetch()}
                isLoading={loading}
                isDisabled={loading}
              >
                Refresh
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
      {renderContent()}
      {isCreateOpen && (
        <SplaDeviceCoresModal device={null} onClose={() => setIsCreateOpen(false)} onSaved={handleSaved} />
      )}
      {editDevice && (
        <SplaDeviceCoresModal
          key={editDevice.id}
          device={editDevice}
          onClose={() => setEditDevice(null)}
          onSaved={handleSaved}
        />
      )}
      {deleteDevice && (
        <SplaDeviceCoresDeleteModal
          device={deleteDevice}
          onClose={() => setDeleteDevice(null)}
          onDelete={handleDelete}
        />
      )}
    </>
  );
}

export default SplaDeviceCoresTab;
